import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  computed,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import { CalculationResult, CellDefinition, Worksheet } from './spreadsheet.models';
import { SpreadsheetService } from './spreadsheet.service';

@Component({
  selector: 'app-spreadsheet',
  standalone: true,
  templateUrl: './spreadsheet.component.html',
  styleUrl: './spreadsheet.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpreadsheetComponent {
  @Output() readonly cellChanged = new EventEmitter<{ address: string; cell: CellDefinition | null }>();

  private readonly spreadsheetService = inject(SpreadsheetService);
  private readonly worksheet = signal<Worksheet>(this.createEmptyWorksheet());
  private readonly calculatedValues = signal<Record<string, unknown>>({});
  readonly calculationErrors = signal<Record<string, string>>({});
  private readonly selectedAddress = signal('A1');
  private readonly editingAddress = signal<string | null>(null);
  private readonly editingValue = signal('');

  readonly rows = computed(() => Array.from({ length: this.worksheet().rows }, (_, index) => index));
  readonly columns = computed(() => Array.from({ length: this.worksheet().columns }, (_, index) => index));
  readonly columnLabels = computed(() => this.columns().map((column) => this.columnLabel(column)));
  readonly currentAddress = this.selectedAddress.asReadonly();
  readonly selectedCellError = computed(() => this.calculationErrors()[this.selectedAddress()] ?? null);
  readonly formulaBarValue = computed(() => {
    if (this.editingAddress() === this.selectedAddress()) {
      return this.editingValue();
    }
    const cell = this.worksheet().cells[this.selectedAddress()];
    return cell ? this.valueAsString(cell.value) : '';
  });

  @Input()
  set initialWorksheet(value: Worksheet | null | undefined) {
    if (value) {
      this.setWorksheet(value);
    }
  }

  constructor() {
    this.recalculate();
  }

  getWorksheet(): Worksheet {
    return this.cloneWorksheet(this.worksheet());
  }

  setWorksheet(worksheet: Worksheet): void {
    const rows = this.validDimension(worksheet.rows);
    const columns = this.validDimension(worksheet.columns);
    const cells: Record<string, CellDefinition> = {};

    for (const [address, definition] of Object.entries(worksheet.cells ?? {})) {
      const indexes = this.spreadsheetService.cellAddressToIndexes(address);
      if (!indexes || indexes.row >= rows || indexes.column >= columns) {
        continue;
      }
      if (definition.value !== undefined) {
        cells[this.spreadsheetService.indexesToCellAddress(indexes.row, indexes.column)] = {
          type: definition.type,
          value: definition.value,
        };
      }
    }

    this.worksheet.set({ rows, columns, cells });
    this.selectedAddress.set('A1');
    this.stopEditing();
    this.recalculate();
  }

  setDimensions(rows: number, columns: number): void {
    const nextRows = this.validDimension(rows);
    const nextColumns = this.validDimension(columns);
    const cells: Record<string, CellDefinition> = {};

    for (const [address, cell] of Object.entries(this.worksheet().cells)) {
      const indexes = this.spreadsheetService.cellAddressToIndexes(address);
      if (indexes && indexes.row < nextRows && indexes.column < nextColumns) {
        cells[address] = { ...cell };
      }
    }

    this.worksheet.set({ rows: nextRows, columns: nextColumns, cells });
    this.recalculate();
  }

  updateDimensions(event: Event, dimension: 'rows' | 'columns'): void {
    const value = Number((event.target as HTMLInputElement).value);
    const current = this.worksheet();
    this.setDimensions(
      dimension === 'rows' ? value : current.rows,
      dimension === 'columns' ? value : current.columns,
    );
  }

  exportWorksheet(): Worksheet {
    return this.getWorksheet();
  }

  importWorksheet(worksheet: Worksheet): void {
    this.setWorksheet(worksheet);
  }

  clearWorksheet(): void {
    this.setWorksheet(this.createEmptyWorksheet());
  }

  recalculate(): void {
    const result: CalculationResult = this.spreadsheetService.recalculate(this.worksheet());
    this.calculatedValues.set(result.values);
    this.calculationErrors.set(result.errors);
  }

  selectCell(address: string): void {
    if (this.editingAddress() && this.editingAddress() !== address) {
      this.commitEdit();
    }
    this.selectedAddress.set(address);
  }

  beginEdit(address: string): void {
    this.selectCell(address);
    this.editingAddress.set(address);
    this.editingValue.set(this.rawCellValue(address));
  }

  updateEditingValue(event: Event): void {
    this.editingValue.set((event.target as HTMLInputElement).value);
  }

  commitEdit(): void {
    const address = this.editingAddress();
    if (!address) {
      return;
    }

    const rawValue = this.editingValue();
    const cells = { ...this.worksheet().cells };
    if (rawValue.trim() === '') {
      delete cells[address];
    } else if (rawValue.trimStart().startsWith('=')) {
      cells[address] = { type: 'formula', value: rawValue.trim() };
    } else {
      const numericValue = Number(rawValue);
      const isNumber = rawValue.trim() !== '' && Number.isFinite(numericValue);
      cells[address] = {
        type: isNumber ? 'number' : 'text',
        value: isNumber ? numericValue : rawValue.trim(),
      };
    }

    this.worksheet.set({ ...this.worksheet(), cells });
    this.stopEditing();
    this.recalculate();
    this.cellChanged.emit({ address, cell: cells[address] ? { ...cells[address] } : null });
  }

  cancelEdit(): void {
    this.stopEditing();
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitEdit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit();
    }
  }

  displayValue(address: string): string {
    if (this.editingAddress() === address) {
      return this.editingValue();
    }
    const cell = this.worksheet().cells[address];
    if (address === this.selectedAddress() && cell?.type === 'formula') {
      return this.valueAsString(cell.value);
    }
    if (this.calculationErrors()[address]) {
      return '#ERROR!';
    }
    const value = this.calculatedValues()[address] ?? cell?.value;
    return this.valueAsString(value);
  }

  isSelected(address: string): boolean {
    return this.selectedAddress() === address;
  }

  hasError(address: string): boolean {
    return Boolean(this.calculationErrors()[address]);
  }

  cellAddress(row: number, column: number): string {
    return this.spreadsheetService.indexesToCellAddress(row, column);
  }

  private rawCellValue(address: string): string {
    const cell = this.worksheet().cells[address];
    return cell ? this.valueAsString(cell.value) : '';
  }

  private stopEditing(): void {
    this.editingAddress.set(null);
    this.editingValue.set('');
  }

  private createEmptyWorksheet(): Worksheet {
    return { rows: 10, columns: 10, cells: {} };
  }

  private cloneWorksheet(source: Worksheet): Worksheet {
    return {
      rows: source.rows,
      columns: source.columns,
      cells: Object.fromEntries(Object.entries(source.cells).map(([address, cell]) => [address, { ...cell }])),
    };
  }

  private validDimension(value: number): number {
    return Number.isInteger(value) && value > 0 ? value : 10;
  }

  private columnLabel(column: number): string {
    return this.spreadsheetService.indexesToCellAddress(0, column).replace(/\d/g, '');
  }

  private valueAsString(value: unknown): string {
    return value === null || value === undefined ? '' : String(value);
  }

}
